import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, pluck, switchMap, tap } from 'rxjs/operators';

import { Oauth2GrantType, oauth2Setting } from './oauth2-setting';
// import { RegExpSet } from '../config/regexp';
import { TokenLog } from '../entities/token-log.model';
import { ApiService } from './api.service';
// import { LoginErrorCode } from '../entities/login-error-code';
// import { HttpOptions } from '../interface/model';
// import { ApiService } from 'api.service';
// import { LoginInfo, TokenLog } from '../entities/token-log.models';
import { CacheService } from './cache-service';
import { HttpOptions } from './http-options';
import { LocalStorageService } from './local-storage.service';
import { LoginInfo } from './login-info';
import { rxSubscriber } from 'rxjs/internal-compatibility';
import { AlertService } from './alert.service';
import { UserService } from './user.service';
// import { ConfigurationService } from './configuration.service';
// import { WINDOW } from './window.service';

const btoaString = btoa(`${oauth2Setting.clientId}:${oauth2Setting.clientSecret}`);

const options: HttpOptions = {
  headers: {
    'Content-Type': 'application/x-www-form-urlencoded',
    Authorization: `Basic ${btoaString}`,
  },
};

export type UserType = 'buyer' | 'vendor' | '';

export interface UserState {
  isLogin: boolean;
  userType: UserType;
  userInfo: TokenLog;
  isLoading: boolean;
  error: any;
  redirectUrl?: string;
  redirectToOriginalUI?: boolean;
  refreshExp?: number;
  tokenLoadedInTime?: number;
  tokenTimeoutNumber?: number;
  userPriv?: string;
}

export class CustomURLEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(key: string): string {
    return encodeURIComponent(key);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(key: string) {
    return decodeURIComponent(key);
  }
}

@Injectable()
export class AuthService {
  private readonly initState: UserState = {
    isLogin: false,
    userType: '',
    userInfo: new TokenLog(),
    isLoading: false,
    error: null,
    refreshExp: null,
  };
  private readonly store = new BehaviorSubject<UserState>(this.initState);
  readonly state$ = this.store.asObservable();

  get state(): UserState {
    return this.store.getValue();
  }

  isLogin$ = this.pluckState('isLogin');
  refreshExp$ = this.pluckState('refreshExp');
  logout$ = this.isLogin$.pipe(
    filter((isLogin) => !isLogin),
    distinctUntilChanged(),
  );
  userType$ = this.pluckState('userType');
  userInfo$ = this.pluckState('userInfo');
  isLoading$ = this.pluckState('isLoading');
  error$ = this.pluckState('error');
  accessToken$ = this.userInfo$.pipe(pluck('access_token'));
  locale$ = this.userInfo$.pipe(pluck('locale'));
  userId$ = this.userInfo$.pipe(pluck('userId'));
  currentUserName$ = this.userInfo$.pipe(pluck('currentUserName'));
  workingDomain$ = this.userInfo$.pipe(pluck('working_domain'));
  isTbyMigration$ = this.userInfo$.pipe(pluck('isTbyMigration'));
  redirectUrl$ = this.pluckState('redirectUrl');

  timezone$ = this.userInfo$.pipe(pluck('newuiTimeZoneCode'));
  dateFormat$ = this.userInfo$.pipe(pluck('dateFormatCode'));
  dateTimeFormat$ = this.userInfo$.pipe(pluck('dateTimeFormatCode'));
  userPriv$ = this.userInfo$.pipe(pluck('userPriv'));

  constructor(
    // @Inject(Window) private readonly window: Window,
    // private readonly configurationService: ConfigurationService,
    private readonly apiService: ApiService,
    private readonly localStorageService: LocalStorageService,
    private readonly cacheService: CacheService,
    private readonly alertService: AlertService,
    private readonly router: Router,
    private readonly userService:UserService,
  ) {
    this.initTokenLog();
  }

  initTokenLog() {
    const tokenLog = new TokenLog();
    if (!!localStorage.getItem('access_token')) {
      Object.keys(tokenLog).forEach((key: string) => {
        tokenLog[key] = JSON.parse(localStorage.getItem(key));
      });
    }
    this.updateTokenLog(tokenLog);
    this.localStorageService.watchData(this.userInfo$);
  }

  login(user: LoginInfo) {
    const loginType= 'password';
    this.clearAuth();

    let params = new HttpParams({ encoder: new CustomURLEncoder() })
      .set('grant_type', loginType)
      .set('username', user.username);
    params = params.set('password', user.password);
   

    const body: string = params.toString();

    this.updateState({ ...this.state, isLoading: true });

    return this.apiService.post<TokenLog>(`/oauth/token?${body}`, "", options).pipe(
      tap((tokenLog) => {
        this.updateTokenLog(tokenLog);
        this.updateState({ ...this.state, error: null });
      }),
      // switchMap(() => this.requestUserAction('login')),
      catchError((error) => {
        const errorMessage =   'Something wrong on server: '+error.status+":"+error.statusText;
        const errorCode = error.code;
        this.updateState({ ...this.state, error: errorMessage });
        this.alertService.error(errorMessage);
        const loginId = user.username;
        switch (errorCode) {
          case 1://LoginErrorCode.changePassword:
            return this.router.navigate(['registration', 'change-password', loginId]);
          default:
            throw error;
        }
      }),
      finalize(() => {
        this.updateState({ ...this.state, isLoading: false });
      }),
    );
  }

  loginBytoken(token: string, refreshToken: string, domainId: string, workingDomain: string, url: string) {
    const tokenLog = new TokenLog();
    tokenLog.access_token = token;
    tokenLog.refresh_token = refreshToken;
    tokenLog.token_type = 'bearer';
    tokenLog.scope = 'read write trust';
    tokenLog.domainId = domainId;
    tokenLog.working_domain = workingDomain;
    this.addRedirectUrl(url);
    this.updateTokenLog(tokenLog);
  }

  refreshToken$(): Observable<TokenLog> {
    const body: string = new HttpParams()
      .set('refresh_token', this.store.getValue().userInfo.refresh_token)
      .set('grant_type', Oauth2GrantType.refreshTokenType)
      .toString();

    this.updateState({ ...this.state, isLoading: true });

    return this.apiService.post<TokenLog>(`/oauth/token?${body}`, '', options).pipe(
      tap((tokenLog) => this.updateTokenLog(tokenLog)),
      finalize(() => {
        this.updateState({ ...this.state, isLoading: false });
      }),
    );
  }

  clearError() {
    this.updateState({ ...this.state, error: null });
  }

  clearAuth() {
    this.cacheService.clearCache();
    this.resetUserState();
    this.clearLocalStorage();
  }

  logout(userId?) {
    this.clearAuth();
    if(userId){
      this.userService.callWSDisConnectRestAPI({id:userId}).subscribe();
    }else{
      this.userId$.pipe().subscribe((userid)=>{
        this.userService.callWSDisConnectRestAPI({id:userid}).subscribe();
      });
    }
    this.router.navigate(['/login']); 
    // this.requestUserAction('logout')
    //   .pipe(
    //     finalize(() => {
    //         this.clearAuth();
    //         this.router.navigate(['/login']); 
    //     }),
    //   )
    //   .subscribe();
  }

  addRedirectUrl(redirectUrl: string) {
    const url = decodeURIComponent(redirectUrl === '/login' ? '' : redirectUrl);
    this.updateState({ ...this.state, redirectUrl: url });
  }

  getCurrentUser(): Observable<any> {
    return this.apiService.get('JBX_URL.userCurrent');
  }

  
  
  checkAuthAndRedirect(): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const isLogin$ = this.isLogin$;
    const refreshExp$ = this.refreshExp$;

    return combineLatest([isLogin$, refreshExp$]).pipe(
      map(([isLogin, refreshExp]) => isLogin && refreshExp > new Date().getTime()),
      tap((pass) => {
        if (pass) {
          return;
        }
        this.addRedirectUrl('/login');
        this.clearAuth();
      }),
      map((pass) => pass || this.router.createUrlTree(['/login'])),
    );
  }


  private clearLocalStorage() {
    this.localStorageService.clear();
  }

  private resetUserState() {
    this.updateState({ ...this.state, ...this.initState });
  }

  private updateTokenLog(tokenLog: TokenLog) {
    const userType = '';//RegExpSet.VENDOR_DOMAIN_ID.test(tokenLog.domain_id) ? 'vendor' : 'buyer';
    const isLogin = !!tokenLog.access_token;
    const refreshExp = (this.parseJwt(tokenLog.refresh_token).exp ?? 0) * 1000;
    const tokenLoadedInTime = new Date().getTime();
    const tokenTimeoutNumber = tokenLog.expires_in * 1000; //in second
    const userPriv = tokenLog.userPriv;

    this.updateState({ ...this.state, userInfo: tokenLog, userType, isLogin, refreshExp, tokenLoadedInTime, tokenTimeoutNumber, userPriv });
  }

  isTokenTimeOut(){
    var timegap: number = new Date().getTime() - this.state.tokenLoadedInTime;
    return timegap>= this.state.tokenTimeoutNumber;
  }

  checkTokenAndRefresh(): Observable<TokenLog>{
    var rxSubscriber:Observable<TokenLog>;
    if(this.isTokenTimeOut()){
      return this.refreshToken$();
    }
    return rxSubscriber;
  }



  // ref: https://stackoverflow.com/a/38552302
  private parseJwt(token: string) {
    if (!token) {
      return {};
    }
    const [, payload] = token.split('.');
    const base64:string = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload =  decodeURIComponent(
      [...atob(base64)].map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0').slice(-2)}`).join(''),
    );

    return JSON.parse(jsonPayload);
  }

  private pluckState<K extends keyof UserState>(key: K): Observable<UserState[K]> {
    return  this.state$.pipe(pluck(key), distinctUntilChanged());
  }

  private updateState(userState: UserState) {
    this.store.next(userState);
  }

  requestUserAction(action: 'login' | 'logout') {
    if(3>2) return;

    // alert(45);
    const params = new HttpParams().set('cbxUserAction', action);
    return this.apiService.post('api/users', null, { params });
  }

  // resetPassword(loginId: string, password: string, confirmPassword: string): Observable<any> {
  //   const params = new HttpParams()
  //     .set('loginId', loginId)
  //     .set('password', password)
  //     .set('confirmPassword', confirmPassword);
  //   return this.apiService.post<any>(resetPassword, {}, { params });
  // }

  // forgotPassword(loginId: string) {
  //   return this.apiService.get<any>(forgotPassword({ loginId }));
  // }

  // changePassword(
  //   loginId: string,
  //   originalPassword: string,
  //   password: string,
  //   confirmPassword: string,
  // ): Observable<any> {
  //   const params = new HttpParams()
  //     .set('loginId', loginId)
  //     .set('originalPassword', originalPassword)
  //     .set('password', password)
  //     .set('confirmPassword', confirmPassword);
  //   return this.apiService.post<any>(changePassword, {}, { params });
  // }
 

  getUserById(idParam: string): Observable<any> {
    return this.apiService.get<any>('JBX_URL.getUserById({ idParam }');
  }
}
